/**
 * Shared calls into AMPNowcertsController's create-prospect / create-opportunity /
 * create-quote endpoints — used by both the "Add an opportunity" wizard and the
 * "Add Quote" sidebar panel opened from an expanded opportunity, so the two don't
 * drift out of sync with each other or with the confirmed NowCerts field names.
 */

const AUTH_URL = process.env.REACT_APP_Auth_Url || '';

/** Buckets an AI Insights current_status (freeform: "Recommended", "Watch",
 *  "NotCompetitive", ...) onto the same on/watch/risk tri-state the app
 *  already uses for winProbability (.sc-health--on/watch/risk) — no fixed
 *  enum confirmed on the backend, so unrecognized values render with no
 *  color rather than guessing wrong. */
export function insightStatusTone(status: string | null | undefined): 'on' | 'watch' | 'risk' | null {
  if (!status) return null;
  const s = status.toLowerCase();
  if (/(recommend|competitive|on.?track|good)/.test(s) && !/not.?competitive/.test(s)) return 'on';
  if (/not.?competitive|risk|declin|reject|critical/.test(s)) return 'risk';
  if (/watch|review|pending|caution/.test(s)) return 'watch';
  return null;
}

function authHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
    NCToken: localStorage.getItem('amsToken') || '',
    'Content-Type': 'application/json',
  };
}

export interface CreateProspectPayload {
  commercialName: string;
  addressLine1?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  email?: string;
  phone?: string;
}

/** Creates a brand-new insured/prospect in NowCerts — backed by AMPNowcertsController's
 *  CreateProspect endpoint, which itself calls NowCerts' Zapier/InsertInsured. */
export async function createProspect(payload: CreateProspectPayload) {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/create-prospect`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

export interface CreateOpportunityPayload {
  lineOfBusinessName: string;
  neededBy?: string;
  opportunityStageName?: string;
  winProbability?: string;
  assignedTo?: string[];
  description?: string;
  agencyCommission?: number;
  insuredDatabaseId?: string;
  insuredEmail?: string;
  insuredFirstName?: string;
  insuredLastName?: string;
  insuredCommercialName?: string;
  createdFromRenewal?: boolean;
  // Set only when updating an existing opportunity (e.g. a stage change from the
  // list view) — NowCerts' InsertOpportunity keys off this to update in place
  // instead of creating a new record. Since it's a full-record call either way,
  // every other field above still needs to carry the opportunity's current
  // values, not just the one being changed.
  databaseId?: string;
  policyNumbers?: string[];
}

/** Creates a brand-new opportunity in NowCerts, or updates an existing one when
 *  `databaseId` is set — backed by AMPNowcertsController's CreateOpportunity
 *  endpoint, which itself calls NowCerts' Zapier/InsertOpportunity. */
export async function createOpportunity(payload: CreateOpportunityPayload) {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/create-opportunity`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

export interface CreateQuotePayload {
  number: string;
  effectiveDate?: string;
  expirationDate?: string;
  carrierName?: string;
  businessType: number;
  premium?: number;
  insuredDatabaseId?: string;
  opportunityIds?: string[];
  note?: string;
  // Base64-encoded, since create-quote is a JSON endpoint shared with the "Add
  // Quote" sidebar panel (which doesn't attach files) — switching it to
  // multipart/form-data would break that caller. The backend decodes and
  // chains it onto the new quote the same way it chains the Note field.
  fileName?: string;
  fileContentBase64?: string;
}

/** Creates a brand-new quote in NowCerts — backed by AMPNowcertsController's
 *  CreateQuote endpoint, which itself calls NowCerts' Zapier/InsertQuote. */
export async function createQuote(payload: CreateQuotePayload) {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/create-quote`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

export interface LineOfBusinessOption {
  id: string;
  name: string;
}

/** Fetches the agency's real lines of business, id and name both — backed by
 *  AMPNowcertsController's GetLinesOfBusiness endpoint, which itself calls NowCerts'
 *  odata/LineOfBusinessList. Each raw record's `databaseId` is that line of business's id.
 *  Passing `name` narrows the NowCerts-side query itself (an odata $filter on name)
 *  rather than filtering a locally-held list — used for the Configuration tab's
 *  search-as-you-type instead of the plain unfiltered load. */
export async function fetchLinesOfBusinessDetailed(name?: string): Promise<LineOfBusinessOption[]> {
  const url = name?.trim()
    ? `${AUTH_URL}/api/AMPNowcerts/lines-of-business?name=${encodeURIComponent(name.trim())}`
    : `${AUTH_URL}/api/AMPNowcerts/lines-of-business`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, NCToken: localStorage.getItem('amsToken') || '' },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  const list = Array.isArray(data?.linesOfBusiness) ? data.linesOfBusiness : [];
  return list
    .map((l: { databaseId?: string; name?: string }) => ({ id: l?.databaseId || '', name: l?.name || '' }))
    .filter((l: LineOfBusinessOption) => !!l.name);
}

/** Names only — what the "Add an opportunity" wizard's Line of Business field needs.
 *  Passing `name` narrows the query server-side the same way fetchLinesOfBusinessDetailed
 *  does, for that field's search-as-you-type. */
export async function fetchLinesOfBusiness(name?: string): Promise<string[]> {
  const list = await fetchLinesOfBusinessDetailed(name);
  return list.map((l) => l.name);
}

/** Fetches the agency's real opportunity stages — backed by AMPNowcertsController's
 *  GetOpportunityStages endpoint, which itself calls NowCerts' odata/OpportunityStageList. */
export async function fetchOpportunityStages(): Promise<string[]> {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/opportunity-stages`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, NCToken: localStorage.getItem('amsToken') || '' },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  const list = Array.isArray(data?.opportunityStages) ? data.opportunityStages : [];
  return list.map((s: { name?: string }) => s?.name).filter((n: unknown): n is string => !!n);
}

/** Reads a File into a bare base64 string (no `data:...;base64,` prefix) — used
 *  to carry a Step 3 attachment inside create-quote's JSON body. */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Attaches a file to an already-existing quote/policy — backed by
 *  AMPNowcertsController's UploadPolicyFile endpoint, which itself calls
 *  NowCerts' Policy/UploadPolicyFile. Used by the Submission Center right
 *  sidebar's Documents section (create-quote's own file chaining, above,
 *  covers the "Add an opportunity" wizard's Step 3 instead). */
export async function uploadPolicyFile(insuredId: string, policyId: string, file: File) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(
    `${AUTH_URL}/api/AMPNowcerts/upload-policy-file?insuredId=${encodeURIComponent(insuredId)}&policyId=${encodeURIComponent(policyId)}`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, NCToken: localStorage.getItem('amsToken') || '' },
      body: form,
    },
  );
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PolicyFileRecord = Record<string, any>;

/** Lists the files already attached to a quote/policy — backed by
 *  AMPNowcertsController's GetPolicyFilesList endpoint, which itself calls
 *  NowCerts' Policy/GetPolicyFilesList. Used only by the Submission Center
 *  right sidebar's Documents section (an existing quote already has a
 *  policyId to list against) — not by the wizard, which is still creating
 *  the quote at that point. */
export async function fetchPolicyFiles(insuredId: string, policyId: string): Promise<PolicyFileRecord[]> {
  const res = await fetch(
    `${AUTH_URL}/api/AMPNowcerts/policy-files?insuredId=${encodeURIComponent(insuredId)}&policyId=${encodeURIComponent(policyId)}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, NCToken: localStorage.getItem('amsToken') || '' } },
  );
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  const response = data?.response;
  // Confirmed shape: { response: { data: { files: [...] } } } — the array/`value`
  // fallbacks below are defensive in case NowCerts ever returns this bare.
  if (Array.isArray(response?.data?.files)) return response.data.files;
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.value)) return response.value;
  return [];
}

export interface CreateTaskPayload {
  title: string;
  status: string;
  priority: string;
  dueDate: string;
  policyDatabaseId?: string;
  insuredDatabaseId?: string;
  // Set only when updating an existing task (e.g. a status change) — same
  // update-in-place convention as CreateOpportunityPayload.databaseId.
  databaseId?: string;
}

/** Creates a task in AMS — what Submission Center's Subjectivities section calls
 *  a "subjectivity" — backed by AMPNowcertsController's CreateTask endpoint,
 *  which itself calls NowCerts' Zapier/InsertTask. */
export async function createTask(payload: CreateTaskPayload) {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/create-task`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TaskRecord = Record<string, any>;

export interface TasksPage {
  tasks: TaskRecord[];
  total: number;
}

/** Lists tasks (Subjectivities) for one insured, narrowed to a policy/quote
 *  number when one is selected, and paginated (skip/top) — backed by
 *  AMPNowcertsController's GetTasksList endpoint, which itself calls NowCerts'
 *  odata/TasksList with count=true (its response already carries `total`).
 *  Used only by the Submission Center right sidebar's Documents-adjacent
 *  Subjectivities section (an existing quote already has a number to filter
 *  on) — not by the wizard, which is still creating the quote at that point. */
export async function fetchTasks(insuredDatabaseId: string, policyNumber?: string, skip = 0, top = 100): Promise<TasksPage> {
  const params = new URLSearchParams({ insuredDatabaseId, skip: String(skip), top: String(top) });
  if (policyNumber) params.set('policyNumber', policyNumber);
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/tasks?${params.toString()}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, NCToken: localStorage.getItem('amsToken') || '' },
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  const tasks = Array.isArray(data?.tasks) ? data.tasks : [];
  return { tasks, total: typeof data?.total === 'number' ? data.total : tasks.length };
}

export interface OpportunityInsights {
  insights: string[];
  nextSteps: string[];
  currentStatus: string | null;
}

/** AI Insights for an opportunity — backed by OpportunitiesSubmissionController's
 *  opportunity-insights endpoint (moved there from MomentumSubmissionTrackerController,
 *  now merged in), which reads the submission-tracker routine's
 *  momentum_submission_tracker_opportunities.opportunity_result for the
 *  signed-in user + this opportunity's NowCerts databaseId (one combined
 *  insights/next-steps set per opportunity, not per quote). Used by the
 *  Submission Center list's expanded opportunity row. */
export async function fetchOpportunityInsights(opportunityDatabaseId: string): Promise<OpportunityInsights> {
  const res = await fetch(
    `${AUTH_URL}/api/OpportunitiesSubmission/opportunity-insights?opportunityDatabaseId=${encodeURIComponent(opportunityDatabaseId)}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
  );
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  return {
    insights: Array.isArray(data?.insights) ? data.insights : [],
    nextSteps: Array.isArray(data?.nextSteps) ? data.nextSteps : [],
    currentStatus: typeof data?.currentStatus === 'string' ? data.currentStatus : null,
  };
}

export interface QuoteInsights {
  insights: string[];
  nextSteps: string[];
  currentStatus: string | null;
}

/** AI Insights for one quote on an opportunity — backed by
 *  OpportunitiesSubmissionController's quote-insights endpoint (moved there
 *  from MomentumSubmissionTrackerController, now merged in), which reads
 *  momentum_submission_tracker_opportunities.quote_result (an array, one
 *  entry per quote_database_id) and picks out the one matching quoteDatabaseId.
 *  Used by QuotePolicyDetailPanel's right-sidebar quote view. */
export async function fetchQuoteInsights(opportunityDatabaseId: string, quoteDatabaseId: string): Promise<QuoteInsights> {
  const res = await fetch(
    `${AUTH_URL}/api/OpportunitiesSubmission/quote-insights?opportunityDatabaseId=${encodeURIComponent(opportunityDatabaseId)}&quoteDatabaseId=${encodeURIComponent(quoteDatabaseId)}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` } },
  );
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  const data = await res.json();
  return {
    insights: Array.isArray(data?.insights) ? data.insights : [],
    nextSteps: Array.isArray(data?.nextSteps) ? data.nextSteps : [],
    currentStatus: typeof data?.currentStatus === 'string' ? data.currentStatus : null,
  };
}

/** Adds a note scoped to just the opportunity (no insured/policy) — backed by
 *  AMPNowcertsController's InsertOpportunityNote endpoint, which calls the
 *  same NowCerts Zapier/InsertNote insertInsuredNote already uses for other
 *  notes, just without an insured_database_id. Used by OpportunityNotesPanel. */
export async function insertOpportunityNote(opportunityId: string, insuredDatabaseId: string, subject: string) {
  const res = await fetch(`${AUTH_URL}/api/AMPNowcerts/opportunity/${encodeURIComponent(opportunityId)}/notes`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ subject, insuredDatabaseId }),
  });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  return res.json();
}

// PolicyBusinessType enum values confirmed against NowCerts' docs.
export const BUSINESS_TYPE_VALUES: Record<string, number> = { 'New Business': 0, 'Renewal': 1, 'Rewrite': 2 };

/** Effective date + 1 year, as a yyyy-mm-dd string for a date input's value —
 *  used to default expiration date when effective date is picked. Returns '' for
 *  an empty/unparseable input so callers can just assign the result straight back. */
export function oneYearLater(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return '';
  d.setFullYear(d.getFullYear() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
